import React, { useMemo, useState } from 'react';
import { X, Car, Calendar, Gauge, MapPin, User, FileText } from 'lucide-react';
import { MobileModalPortal } from '../../components/common/MobileModalPortal';
import type { ConsultancyPersistedVehicle, ConsultancySaleRecord } from '../../lib/consultancyStore';
import { mediaSrc } from '../../lib/mediaUrl';

type VehicleDetailModalProps = {
  vehicle: ConsultancyPersistedVehicle;
  sale?: ConsultancySaleRecord | null;
  onClose: () => void;
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  const empty = value == null || value === '' || value === '—';
  return (
    <div className="border-b border-zinc-100 py-2.5 last:border-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</dt>
      <dd className={`mt-0.5 text-sm ${empty ? 'text-zinc-400' : 'font-medium text-zinc-900'}`}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
}

function statusLabel(status: ConsultancyPersistedVehicle['status']) {
  if (status === 'available') return 'Available';
  if (status === 'sold') return 'Sold';
  return 'Service';
}

function statusClass(status: ConsultancyPersistedVehicle['status']) {
  if (status === 'available') return 'border-emerald-200 bg-emerald-50 text-emerald-900';
  if (status === 'sold') return 'border-zinc-200 bg-zinc-100 text-zinc-700';
  return 'border-amber-200 bg-amber-50 text-amber-950';
}

export function VehicleDetailModal({ vehicle, sale, onClose }: VehicleDetailModalProps) {
  const photos = useMemo(() => {
    const list = vehicle.images?.length ? vehicle.images : vehicle.imageUrl ? [vehicle.imageUrl] : [];
    return list.map((src) => mediaSrc(src)).filter(Boolean);
  }, [vehicle.images, vehicle.imageUrl]);

  const [activePhoto, setActivePhoto] = useState(0);
  const chassis = vehicle.chasisNumber || vehicle.model || '';
  const acquiredLabel = vehicle.boughtAt
    ? new Date(vehicle.boughtAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  const salePrice = sale ? Math.max(0, parseFloat(String(sale.sellingPrice ?? '')) || 0) : 0;
  const saleDate = sale?.recordedAt
    ? new Date(String(sale.recordedAt)).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  return (
    <MobileModalPortal onClose={onClose}>
      <div className="max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom,0px)))] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-zinc-200 bg-white shadow-xl sm:max-h-[90vh] sm:rounded-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-100 bg-white/95 px-4 py-3 backdrop-blur-sm">
          <div className="min-w-0 pr-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">Vehicle details</p>
            <h2 className="truncate text-base font-semibold text-zinc-950">{vehicle.name || 'Vehicle'}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-zinc-100">
            {photos.length > 0 ? (
              <img
                src={photos[activePhoto] ?? photos[0]}
                alt={vehicle.name || 'Vehicle'}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-zinc-400">
                <Car className="h-10 w-10" aria-hidden />
                <span className="text-xs">No photos</span>
              </div>
            )}
            <span
              className={`absolute right-2 top-2 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase shadow-sm ${statusClass(vehicle.status)}`}
            >
              {statusLabel(vehicle.status)}
            </span>
          </div>

          {photos.length > 1 ? (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  type="button"
                  onClick={() => setActivePhoto(i)}
                  className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${
                    activePhoto === i ? 'border-emerald-600' : 'border-zinc-200'
                  }`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          ) : null}

          <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-3">
            <p className="text-2xl font-bold tabular-nums text-zinc-950">
              ₹{vehicle.price.toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-zinc-500">Amount invested / landed cost</p>
          </div>

          <dl className="rounded-xl border border-zinc-200 px-3">
            <DetailRow label="Deal reference" value={vehicle.dealRef} />
            <DetailRow label="Vehicle name" value={vehicle.name} />
            <DetailRow label="Seller / source" value={vehicle.sellerName} />
            <DetailRow label="Vehicle number" value={vehicle.vehicleNumber} />
            <DetailRow label="Chassis number" value={chassis} />
            <DetailRow label="Engine number" value={vehicle.engineNumber} />
            <DetailRow
              label="Kilometer driven"
              value={vehicle.kms != null ? `${vehicle.kms.toLocaleString('en-IN')} km` : null}
            />
            <DetailRow label="C-book number" value={vehicle.cBookNumber} />
            <DetailRow label="Repairs / recon" value={vehicle.repairs} />
            <DetailRow label="Year" value={vehicle.year ? String(vehicle.year) : null} />
            {vehicle.location ? <DetailRow label="Location" value={vehicle.location} /> : null}
            <DetailRow label="Acquired on" value={acquiredLabel} />
          </dl>

          {vehicle.status === 'sold' && sale ? (
            <section className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-900">
                <FileText className="h-3.5 w-3.5" aria-hidden />
                Sale record
              </h3>
              <dl className="mt-2 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-600">Buyer</span>
                  <span className="font-medium text-zinc-900">{String(sale.customerName || '—')}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-600">Selling price</span>
                  <span className="font-semibold tabular-nums text-emerald-900">
                    ₹{salePrice.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-600">Sold on</span>
                  <span className="font-medium text-zinc-900">{saleDate}</span>
                </div>
                {sale.acquisitionCostAtSale != null ? (
                  <div className="flex justify-between gap-3">
                    <span className="text-zinc-600">Margin</span>
                    <span className="font-semibold tabular-nums text-zinc-900">
                      ₹{(salePrice - Number(sale.acquisitionCostAtSale)).toLocaleString('en-IN')}
                    </span>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}

          <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden />
              {vehicle.year || '—'}
            </div>
            <div className="flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5" aria-hidden />
              {vehicle.kms != null ? `${vehicle.kms.toLocaleString('en-IN')} km` : '—'}
            </div>
            {vehicle.location ? (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" aria-hidden />
                {vehicle.location}
              </div>
            ) : null}
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" aria-hidden />
              {vehicle.sellerName || '—'}
            </div>
          </div>

        </div>

        <div className="sticky bottom-0 border-t border-zinc-100 bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
          <button type="button" onClick={onClose} className="app-btn-secondary w-full">
            Close
          </button>
        </div>
      </div>
    </MobileModalPortal>
  );
}
